import { useState } from 'react';
import type { DrCheckProbe, DrCheckReport } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useDrCheck } from '../api/drCheck.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  PageHeader,
  Panel,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/dr-check` — DR readiness drill surface. Epic #109 P3.19.
 *
 * Manual "Run drill" button triggers the backend aggregator; result
 * renders as a per-probe table + overall pass/fail banner. No history
 * table persisted in phase 1 — ops runs the drill on demand and
 * archives the output externally if needed.
 */

export function DrCheckPage(): JSX.Element {
  const t = useT();
  const mutation = useDrCheck();
  const toast = useToast();
  const [lastReport, setLastReport] = useState<DrCheckReport | null>(null);

  async function handleRun(): Promise<void> {
    try {
      const report = await mutation.mutateAsync();
      setLastReport(report);
      if (report.overall === 'pass') {
        toast.success(t('dr_check.toast.pass'));
      } else {
        toast.error(t('dr_check.toast.fail'));
      }
    } catch (err) {
      const m =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      toast.error(m);
    }
  }

  return (
    <section className="flex flex-col gap-6" data-testid="dr-check-page">
      <PageHeader title={t('dr_check.title')} sub={t('dr_check.subtitle')} />

      <Panel
        testId="dr-check-runner-card"
        caption={t('dr_check.runner.title')}
        padded
        actions={
          <Button
            variant="primary"
            onClick={() => void handleRun()}
            loading={mutation.isPending}
            data-testid="dr-check-run"
          >
            {mutation.isPending ? t('dr_check.action.running') : t('dr_check.action.run')}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 text-body text-paper-mid">{t('dr_check.runner.body')}</p>
          {lastReport === null ? (
            <p className="m-0 text-dense text-paper-low" data-testid="dr-check-idle">
              {t('dr_check.idle')}
            </p>
          ) : (
            <ReportBlock report={lastReport} />
          )}
        </div>
      </Panel>
    </section>
  );
}

interface ReportBlockProps {
  readonly report: DrCheckReport;
}

function ReportBlock({ report }: ReportBlockProps): JSX.Element {
  const t = useT();
  const overallPass = report.overall === 'pass';
  return (
    <div className="flex flex-col gap-4">
      <div
        className={
          overallPass
            ? 'rounded-lg border border-ok-line bg-ok-dim/50 p-4 text-body text-paper'
            : 'rounded-lg border border-bad-line bg-bad-dim p-4 text-body text-bad-bright'
        }
        role={overallPass ? 'status' : 'alert'}
        data-testid="dr-check-overall-banner"
        data-outcome={report.overall}
      >
        <strong className={overallPass ? 'font-semibold text-ok' : 'font-semibold'}>
          {overallPass
            ? t('dr_check.overall.pass_title')
            : t('dr_check.overall.fail_title')}
        </strong>{' '}
        {t('dr_check.overall.body', {
          started: report.started_at,
          duration: String(report.duration_ms),
        })}
      </div>
      <Table testId="dr-check-probes-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('dr_check.column.probe')}</Th>
            <Th>{t('dr_check.column.outcome')}</Th>
            <Th num>{t('dr_check.column.latency')}</Th>
            <Th>{t('dr_check.column.detail')}</Th>
          </Tr>
        </THead>
        <TBody>
          {report.probes.map((p) => (
            <ProbeRow key={p.name} probe={p} />
          ))}
        </TBody>
      </Table>
    </div>
  );
}

interface ProbeRowProps {
  readonly probe: DrCheckProbe;
}

function ProbeRow({ probe }: ProbeRowProps): JSX.Element {
  const t = useT();
  const isPass = probe.outcome === 'pass';
  return (
    <Tr data-testid={`dr-check-row-${probe.name}`} data-outcome={probe.outcome}>
      <Td mono emphasis>
        {probe.name}
      </Td>
      <Td>
        <Chip tone={isPass ? 'ok' : 'bad'}>
          {isPass ? t('dr_check.probe.pass') : t('dr_check.probe.fail')}
        </Chip>
      </Td>
      <Td num>{probe.latency_ms}ms</Td>
      <Td>
        {probe.error !== null ? (
          <p className="m-0 font-mono text-micro text-bad">{probe.error}</p>
        ) : probe.detail !== null ? (
          <pre className="m-0 whitespace-pre-wrap font-mono text-micro text-paper-low">
            {JSON.stringify(probe.detail, null, 2)}
          </pre>
        ) : (
          <span className="text-micro text-paper-low">—</span>
        )}
      </Td>
    </Tr>
  );
}
